import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { TutorialConfig, TutorialState, TutorialStep, TutorialLocalStorage } from './tutorial.interface';

@Injectable({
  providedIn: 'root'
})
export class TutorialService {
  private readonly STORAGE_KEY = 'tutorial_data';
  private tutorialState: BehaviorSubject<TutorialState> = new BehaviorSubject<TutorialState>({
    ativo: false,
    tutorialId: '',
    stepAtual: -1,
    totalSteps: 0,
    pausado: false,
    elemento: null,
    posicao: { x: 0, y: 0 },
    tempoRestante: 0,
    tempoTotalStep: 0
  });

  private tutorialConfig: TutorialConfig | null = null;
  private timeoutId: any = null;
  private countdownInterval: any = null;
  private stepChangeSubject = new Subject<{ step: TutorialStep; stepIndex: number }>();
  private tutorialResetSubject = new Subject<string>();

  public state$: Observable<TutorialState> = this.tutorialState.asObservable();
  public stepChange$: Observable<{ step: TutorialStep; stepIndex: number }> = this.stepChangeSubject.asObservable();
  public tutorialReset$: Observable<string> = this.tutorialResetSubject.asObservable();

  constructor() {}

  iniciarTutorial(config: TutorialConfig, force: boolean = false): boolean {
    if (!force && this.tutorialFoiOcultado(config.id)) {
      return false;
    }

    this.tutorialConfig = config;
    const state = this.tutorialState.value;
    
    this.tutorialState.next({
      ...state,
      ativo: true,
      tutorialId: config.id,
      stepAtual: 0,
      totalSteps: config.steps.length,
      pausado: false
    });

    this.executarStep(0);
    return true;
  }

  pararTutorial(): void {
    this.limparTimeout();
    this.limparCountdown();
    this.limparDadosFalsos();
    
    const state = this.tutorialState.value;
    this.tutorialState.next({
      ...state,
      ativo: false,
      tutorialId: this.tutorialConfig?.id || '',
      stepAtual: 0,
      totalSteps: 0,
      pausado: false,
      elemento: null,
      tempoRestante: 0,
      tempoTotalStep: 0
    });

    // Executa o callback ao concluir/parar o tutorial
    if (this.tutorialConfig?.aoConcluir) {
      this.tutorialConfig.aoConcluir();
    }

    this.tutorialConfig = null;
  }

  proximoStep(): void {
    if (!this.tutorialConfig) return;

    const state = this.tutorialState.value;
    const proximoIndex = state.stepAtual + 1;

    if (proximoIndex < this.tutorialConfig.steps.length) {
      this.executarStep(proximoIndex);
    } else {
      this.concluirTutorial();
    }
  }

  stepAnterior(): void {
    if (!this.tutorialConfig) return;

    const state = this.tutorialState.value;
    const anteriorIndex = state.stepAtual - 1;

    if (anteriorIndex >= 0) {
      this.executarStep(anteriorIndex);
    }
  }

  irParaStep(stepIndex: number): void {
    if (!this.tutorialConfig || stepIndex < 0 || stepIndex >= this.tutorialConfig.steps.length) {
      return;
    }

    this.executarStep(stepIndex);
  }

  pausarTutorial(): void {
    this.limparTimeout();
    this.limparCountdown();
    const state = this.tutorialState.value;
    this.tutorialState.next({
      ...state,
      pausado: true
    });
  }

  continuarTutorial(): void {
    const state = this.tutorialState.value;
    this.tutorialState.next({
      ...state,
      pausado: false
    });
    
    // Retoma o countdown se houver tempo restante
    if (state.tempoRestante && state.tempoRestante > 0) {
      this.iniciarCountdown(state.tempoRestante);
    }
  }

  marcarComoOcultado(tutorialId: string): void {
    const dados = this.obterDadosStorage();
    if (!dados.tutoriaisOcultos.includes(tutorialId)) {
      dados.tutoriaisOcultos.push(tutorialId);
      dados.configuracoes[tutorialId] = {
        naoMostrarNovamente: true,
        ultimoStepCompletado: -1
      };
      this.salvarDadosStorage(dados);
    }
  }

  marcarComoCompletado(tutorialId: string): void {
    const dados = this.obterDadosStorage();
    if (!dados.tutoriaisCompletados.includes(tutorialId)) {
      dados.tutoriaisCompletados.push(tutorialId);
      dados.configuracoes[tutorialId] = {
        naoMostrarNovamente: false,
        ultimoStepCompletado: this.tutorialConfig?.steps.length || 0
      };
      this.salvarDadosStorage(dados);
    }
  }

  tutorialFoiOcultado(tutorialId: string): boolean {
    const dados = this.obterDadosStorage();
    return dados.tutoriaisOcultos.includes(tutorialId) || 
           dados.configuracoes[tutorialId]?.naoMostrarNovamente || false;
  }

  tutorialFoiCompletado(tutorialId: string): boolean {
    const dados = this.obterDadosStorage();
    return dados.tutoriaisCompletados.includes(tutorialId);
  }

  resetarTutorial(tutorialId: string): void {
    const dados = this.obterDadosStorage();
    dados.tutoriaisOcultos = dados.tutoriaisOcultos.filter(id => id !== tutorialId);
    dados.tutoriaisCompletados = dados.tutoriaisCompletados.filter(id => id !== tutorialId);
    delete dados.configuracoes[tutorialId];
    this.salvarDadosStorage(dados);
    
    // Emitir evento de reset
    this.tutorialResetSubject.next(tutorialId);
  }

  private executarStep(stepIndex: number): void {
    if (!this.tutorialConfig) return;

    this.limparTimeout();
    this.limparCountdown();
    
    const stepAtual = this.tutorialConfig.steps[stepIndex];
    const stepAnterior = stepIndex > 0 ? this.tutorialConfig.steps[stepIndex - 1] : null;

    // Executa callback de saída do step anterior
    if (stepAnterior?.aoSair) {
      stepAnterior.aoSair();
    }

    // Limpa dados falsos do step anterior
    if (stepAnterior?.limparDadosFalsos) {
      stepAnterior.limparDadosFalsos();
    }

    // Cria dados falsos se necessário
    if (stepAtual.criarDadosFalsos) {
      stepAtual.criarDadosFalsos();
    }

    // Executa callback de entrada do step atual
    if (stepAtual.aoEntrar) {
      stepAtual.aoEntrar();
    }
    // Aguarda elemento aparecer se necessário
    if (stepAtual.aguardarElemento && stepAtual.elemento) {
      this.aguardarElemento(stepAtual.elemento, stepAtual.tempoEspera || 5000).then(() => {
        this.atualizarStateStep(stepIndex, stepAtual);
      });
    } else {
      this.atualizarStateStep(stepIndex, stepAtual);
    }
  }

  private atualizarStateStep(stepIndex: number, step: TutorialStep): void {
    const elemento = step.elemento ? document.querySelector(step.elemento) as HTMLElement : null;
    let posicao = { x: 0, y: 0 };

    if (elemento) {
      this.scrollToElement(elemento);

      const rect = elemento.getBoundingClientRect();
      posicao = {
        x: rect.left + (rect.width / 2),
        y: rect.top + (rect.height / 2)
      };

      // Scroll to element to ensure it's visible
    }

    const state = this.tutorialState.value;
    const tempoEspera = step.tempoEspera || this.tutorialConfig?.tempoEntreSteps || 0;
    this.tutorialState.next({
      ...state,
      stepAtual: stepIndex,
      elemento,
      posicao,
      tempoRestante: tempoEspera,
      tempoTotalStep: tempoEspera
    });

    // Emite no próximo tick para garantir que o componente já esteja subscrito
    setTimeout(() => {
      this.stepChangeSubject.next({ step, stepIndex });
    }, 0);

    // Programa próximo step se há tempo configurado
    if (tempoEspera && stepIndex < (this.tutorialConfig?.steps.length || 0) - 1 && !this.tutorialState.value.pausado) {
      this.iniciarCountdown(tempoEspera);
    }
  }

  private scrollToElement(elemento: HTMLElement): void {
    // Use scrollIntoView with block: 'center' to center the element in viewport
    elemento.scrollIntoView({
      behavior: 'instant' as ScrollBehavior,
      block: 'center',
      inline: 'nearest'
    });
  }

  private async aguardarElemento(seletor: string, timeout: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const elemento = document.querySelector(seletor);
      if (elemento) {
        resolve();
        return;
      }

      let tentativas = 0;
      const maxTentativas = timeout / 100;

      const intervalo = setInterval(() => {
        const elemento = document.querySelector(seletor);
        if (elemento) {
          clearInterval(intervalo);
          resolve();
        } else if (tentativas >= maxTentativas) {
          clearInterval(intervalo);
          reject(new Error(`Elemento ${seletor} não encontrado em ${timeout}ms`));
        }
        tentativas++;
      }, 100);
    });
  }

  private concluirTutorial(): void {
    if (this.tutorialConfig) {
      this.marcarComoCompletado(this.tutorialConfig.id);
      // O callback aoConcluir será chamado por pararTutorial()
    }
    this.pararTutorial();
  }

  private limparTimeout(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private limparCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private iniciarCountdown(tempoTotal: number): void {
    this.limparCountdown();
    
    const state = this.tutorialState.value;
    this.tutorialState.next({
      ...state,
      tempoRestante: tempoTotal,
      tempoTotalStep: tempoTotal
    });

    this.countdownInterval = setInterval(() => {
      const currentState = this.tutorialState.value;
      const novoTempo = (currentState.tempoRestante || 0) - 1000;
      
      if (novoTempo <= 0) {
        this.limparCountdown();
        this.proximoStep();
      } else {
        this.tutorialState.next({
          ...currentState,
          tempoRestante: novoTempo
        });
      }
    }, 1000);
  }

  private limparDadosFalsos(): void {
    if (this.tutorialConfig) {
      this.tutorialConfig.steps.forEach(step => {
        if (step.limparDadosFalsos) {
          step.limparDadosFalsos();
        }
      });
    }
  }

  private obterDadosStorage(): TutorialLocalStorage {
    const dados = localStorage.getItem(this.STORAGE_KEY);
    if (dados) {
      try {
        return JSON.parse(dados);
      } catch (error) {
        console.error('Erro ao parsear dados do tutorial:', error);
      }
    }

    return {
      tutoriaisCompletados: [],
      tutoriaisOcultos: [],
      configuracoes: {}
    };
  }

  private salvarDadosStorage(dados: TutorialLocalStorage): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dados));
    } catch (error) {
      console.error('Erro ao salvar dados do tutorial:', error);
    }
  }
} 